import { faHistory } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import { Fragment, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";

import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig, { useTenantFormOptions } from "../../../../actions/Tenants/config/configHook";
import { useFields } from "../../../../hooks";
import { useAllocatedLayersForCycles } from "../../../../hooks/useFacilityLocationQueries";
import { getFilledArrayOrDefault } from "../../../../utils/getFilledArrayOrDefault";
import { CategoryColorBadge } from "../../../Helper/Badges";
import { Button } from "../../../Helper/Button";
import { formatDateTime } from "../../Layers/fields/formatters";
import useRipeningRoom from "../hooks/useRipeningRoom";
import { formatHistoryChangeValue } from "./cycleHistory";
import { getCycleStartCommand, getCycleStartState } from "./ripeningProgramCycle";

export default function RipeningRoomRipeningCycles() {

    const { ripeningCycles, activeRipeningCycle, currentLocation, setSearchParam, selectedRipeningCycleId, fruit_type_config, fruit_type, cycleStartTracker } = useRipeningRoom();
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [accordionCycleId, setAccordionCycleId] = useState<string | null>(null);
    const [actorModalOpen, setActorModalOpen] = useState(false);
    const [selectedActor, setSelectedActor] = useState<any | null>(null);
    const config = useConfig();
    const form_options = useTenantFormOptions();
    const { applyDisplayFilterLayerFields } = useFields();

    const cycleHistoryFields = applyDisplayFilterLayerFields(config?.root_config?.ripening_room_cycle_history_fields, {
        fruit_type,
    });
    const cycleSidebarFields = applyDisplayFilterLayerFields(config?.root_config?.ripening_room_cycle_sidebar_fields, {
        fruit_type,
    });

    // Allocated pallets per cycle, for columns that aggregate layer meta across a cycle
    // (e.g. PO via LIST_CHILDREN_META). Only fetched once the history modal is open.
    const allocatedLayersByCycleId = useAllocatedLayersForCycles(
        currentLocation?.location_id,
        ripeningCycles,
        historyModalOpen,
    );

    const ripeningRoomForCycle = (cycle: any) => ({
        ...(currentLocation ?? {}),
        ...cycle,
        ...(cycle?.meta && typeof cycle.meta === "object" ? cycle.meta : {}),
    });

    if (!Array.isArray(ripeningCycles) || ripeningCycles.length === 0) {
        return (
            <div className="text-muted small">
                No ripening cycles recorded yet.
            </div>
        );
    }

    // * Local helper kept (not replaced with formatters.formatDate) — this is
    // * an intentionally compact "DD MMM" range label without year.
    const formatShortDate = (value?: string | Date) => (value ? dayjs(value).format("DD MMM") : "-");
    const formatActionLabel = (action?: string) => {
        if (action === "start") return "START";
        if (action === "edit") return "EDIT";
        if (action === "note") return "NOTE";
        return (action || "event").toUpperCase();
    };

    // * An active cycle whose program the room has not (yet) started is not shown as running.
    const activeCycleBadge = (cycle: any) => {
        const tracked = cycleStartTracker?.cycleStart;
        const state = tracked && tracked.cycleId === cycle.id
            ? getCycleStartState(tracked.status, tracked.watched ? null : tracked.startedAt)
            : getCycleStartState(getCycleStartCommand(cycle)?.status, cycle.ripening_start_date);
        if (state === "starting") return <span className="badge text-bg-warning">Starting…</span>;
        if (state === "failed") {
            return (
                <button
                    type="button"
                    className="badge text-bg-danger border-0"
                    title="Show why the ripening room did not start"
                    onClick={cycleStartTracker?.openCycleStartFeedback}
                >
                    Start failed
                </button>
            );
        }
        return <span className="badge text-bg-success">Active</span>;
    };

    const handleClick = (cycleId: string) => {
        setSearchParam("ripening_cycle_id", cycleId);
    };

    // Get the cycle to display: selected cycle if exists, otherwise active cycle
    const displayedCycle = selectedRipeningCycleId
        ? ripeningCycles.find((cycle) => cycle.id === selectedRipeningCycleId)
        : ripeningCycles.find((cycle) => cycle.active);

    const metaFieldLabelByName = (() => {
        // For audit/history labels, use the cycle forms as the source of truth for field labels.
        const startCycleFormFields = applyDisplayFilterFields(config.root_config?.ripening_room_start_cycle_form, { fruit_type });
        const editCycleFormFields = applyDisplayFilterFields(config.root_config?.ripening_room_edit_cycle_form, { fruit_type });
        const allCycleFormFields = [...startCycleFormFields, ...editCycleFormFields];

        const pairs: Array<[string, string]> = allCycleFormFields.map((f: any) => {
            const name = f?.name;
            const label = f?.label || name;
            return [String(name), String(label)] as [string, string];
        });

        // If a field appears multiple times, keep the first configured label.
        return new Map(
            pairs
                .filter(([name]) => name != null && String(name) !== "")
                .filter(([name], idx) => idx === pairs.findIndex(([n]) => n === name))
        );
    })();

    const getChangeLabel = (key: string) => {
        if (key === "ripening_start_date") return "Start date";
        if (key === "ripening_end_date") return "End date";
        if (key === "ripening_target") return "Target class";
        if (key === "season") return "Season";
        if (key === "active") return "Active";
        if (key === "meta.controller_command") return "Ripening room start";
        if (key.startsWith("meta.")) {
            const metaKey = key.replace(/^meta\./, "");
            const label = metaFieldLabelByName.get(metaKey);
            return label != null && String(label) !== "" ? String(label) : metaKey;
        }
        return key;
    };

    const formatChangeValue = (key: string, rawValue: any) => formatHistoryChangeValue(key, rawValue, config.get_form_option_label);


    const historyTableColSpan = 4 + cycleHistoryFields.length;

    const renderCycleHistoryValueCells = (cycle: any) => cycleHistoryFields.map((field: any, index: number) => (
        <td key={field.name ?? index}>
            {field.set_value({
                config,
                ripeningRoom: ripeningRoomForCycle(cycle),
                children: allocatedLayersByCycleId[cycle.id],
                form_options,
            }).display_component()}
        </td>
    ));

    const renderCycleRow = (cycle: any) => {
        const isSelected = selectedRipeningCycleId === cycle.id;
        const isCurrent = cycle.active || activeRipeningCycle?.id === cycle.id;

        return (
            <Fragment key={cycle.id}>
                <tr className={isSelected ? "table-primary" : ""}>
                    <td>
                        <b>{formatShortDate(cycle.ripening_start_date)}</b> - <b>{formatShortDate(cycle.ripening_end_date)}</b>
                    </td>
                    <td>
                        {isCurrent ? (
                            activeCycleBadge(cycle)
                        ) : (
                            <span className="badge text-bg-secondary">Inactive</span>
                        )}
                    </td>
                    <td>
                        <CategoryColorBadge value={cycle.ripening_target} categories={getFilledArrayOrDefault(fruit_type_config?.ripening_fields?.[0]?.categories)} />
                    </td>
                    {renderCycleHistoryValueCells(cycle)}
                    <td className="text-end">
                        <Button
                            color={isSelected ? "primary" : "light"}
                            size="sm"
                            onClick={() => {
                                handleClick(cycle.id);
                            }}
                        >
                            {isSelected ? "Selected" : "View"}
                        </Button>
                        <Button
                            color="light"
                            size="sm"
                            className="ms-2"
                            icon={faHistory}
                            onClick={() => setAccordionCycleId((prev) => (prev === cycle.id ? null : cycle.id))}
                        />
                    </td>
                </tr>
                {accordionCycleId === cycle.id && (
                    <tr>
                        <td colSpan={historyTableColSpan}>
                            <div className="border-top pt-2">
                                {(() => {
                                    const cycleMetaLog = Array.isArray(cycle?.meta?.meta_log) ? cycle.meta.meta_log : [];
                                    const sortedMetaLog = [...cycleMetaLog].sort((a: any, b: any) => {
                                        const aTs = a?.ts ? dayjs(a.ts).valueOf() : 0;
                                        const bTs = b?.ts ? dayjs(b.ts).valueOf() : 0;
                                        return bTs - aTs;
                                    });

                                    if (sortedMetaLog.length === 0) {
                                        return <div className="text-muted small">No history entries yet.</div>;
                                    }

                                    return (
                                        <div className="list-group mt-2">
                                            {sortedMetaLog.map((entry: any, idx: number) => {
                                                let changes: Record<string, { from?: any; to?: any }> = {};
                                                if (entry?.changes && typeof entry.changes === "object") {
                                                    changes = entry.changes;
                                                }
                                                const changeKeys = Object.keys(changes);
                                                return (
                                                    <div key={`${entry?.ts || idx}-${idx}`} className="list-group-item">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span
                                                                    className="badge text-bg-light text-uppercase d-inline-flex justify-content-center"
                                                                    style={{ minWidth: 64 }}
                                                                >
                                                                    {formatActionLabel(entry?.action)}
                                                                </span>
                                                                <Button
                                                                    color="link"
                                                                    size="sm"
                                                                    className="p-0"
                                                                    onClick={() => {
                                                                        setSelectedActor(entry.actor);
                                                                        setActorModalOpen(true);
                                                                    }}
                                                                >
                                                                    <span className="fw-bold">
                                                                        {renderActorFirstName(entry.actor)}
                                                                    </span>
                                                                </Button>
                                                            </div>
                                                            <div className="text-muted small">{formatDateTime(entry?.ts)}</div>
                                                        </div>
                                                        {renderHistoryEntryChanges(changeKeys, changes)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        </td>
                    </tr>
                )}
            </Fragment>
        );
    };

    const renderActorFirstName = (actor: any) => {
        return actor?.first_name || actor?.last_name || actor?.name || "Unknown";
    };

    const renderHistoryEntryChanges = (changeKeys: string[], changes: Record<string, { from?: any; to?: any }>) => {
        const meaningfulKeys = changeKeys.filter((key) => {
            const fromVal = formatChangeValue(key, changes[key]?.from);
            const toVal = formatChangeValue(key, changes[key]?.to);
            return fromVal !== toVal;
        });
        if (meaningfulKeys.length === 0) {
            return <div className="text-muted small mt-2">No recorded field changes.</div>;
        }
        return (
            <div className="mt-2">
                <table className="w-100 small">
                    <tbody>
                        {meaningfulKeys.map((key) => {
                            const fromDisplay = formatChangeValue(key, changes[key]?.from);
                            const toDisplay = formatChangeValue(key, changes[key]?.to);

                            let valueNode = (
                                <>
                                    <span className="text-muted">
                                        {fromDisplay}
                                    </span>
                                    <span className="mx-2">→</span>
                                    <span className="fw-bold">
                                        {toDisplay}
                                    </span>
                                </>
                            );

                            if (fromDisplay === "-") {
                                valueNode = (
                                    <span className="fw-bold">
                                        {toDisplay}
                                    </span>
                                );
                            }

                            if (key === "meta.room_notes") {
                                valueNode = (
                                    <div className="fw-bold text-start" style={{ whiteSpace: "pre-wrap" }}>
                                        {toDisplay}
                                    </div>
                                );
                            }

                            return (
                                <tr key={key}>
                                    <td className="pe-2 text-muted" style={{ width: "40%" }}>
                                        {getChangeLabel(key)}
                                    </td>
                                    <td className="text-start">
                                        {valueNode}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderCycleSummaryTable = () => {
        if (!displayedCycle) {
            return (
                <table className="w-100">
                    <tbody>
                        {["Cycle", "Status", "Target class", ...cycleSidebarFields.map((f: any) => f.label || f.name)].map((col) => (
                            <tr key={col}>
                                <td className="fw-bold text-no-wrap">{col}</td>
                                <td className="text-end">-</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        const isDisplayedCycleActive = displayedCycle.active || activeRipeningCycle?.id === displayedCycle.id;
        let statusBadge = <span className="badge text-bg-secondary">Inactive</span>;
        if (isDisplayedCycleActive) {
            statusBadge = activeCycleBadge(displayedCycle);
        }

        return (
            <table className="w-100">
                <tbody>
                    <tr>
                        <td className="fw-bold text-no-wrap">Cycle</td>
                        <td className="text-end">
                            <b>{formatShortDate(displayedCycle.ripening_start_date)}</b> - <b>{formatShortDate(displayedCycle.ripening_end_date)}</b>
                        </td>
                    </tr>
                    <tr>
                        <td className="fw-bold text-no-wrap">Status</td>
                        <td className="text-end">{statusBadge}</td>
                    </tr>
                    <tr>
                        <td className="fw-bold text-no-wrap">Target class</td>
                        <td className="text-end">
                            <CategoryColorBadge value={displayedCycle.ripening_target} categories={getFilledArrayOrDefault(fruit_type_config?.ripening_fields?.[0]?.categories)} />
                        </td>
                    </tr>
                    {cycleSidebarFields.map((field: any, index: number) => (
                        <tr key={field.name ?? index}>
                            <td className="fw-bold text-no-wrap">{field.label || field.name}</td>
                            <td className="text-end">
                                {field.set_value({
                                    config,
                                    ripeningRoom: ripeningRoomForCycle(displayedCycle),
                                    form_options,
                                }).display_component()}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div>
            {renderCycleSummaryTable()}
            <div className="d-flex justify-content-end align-items-center mt-3">

                <Button color="link" size="sm" onClick={() => setHistoryModalOpen(true)}>
                    Open history
                </Button>
            </div>

            <Modal isOpen={historyModalOpen} size="lg" toggle={() => setHistoryModalOpen(false)}>
                <ModalHeader toggle={() => setHistoryModalOpen(false)}>Ripening Cycles History</ModalHeader>
                <ModalBody>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Cycle</th>
                                <th>Status</th>
                                <th>Target class</th>
                                {cycleHistoryFields.map((field: any, index: number) => (
                                    <th key={field.name ?? index}>{field.label}</th>
                                ))}
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ripeningCycles.map((cycle) => renderCycleRow(cycle))}
                        </tbody>
                    </table>

                    {/* History is shown inline per cycle row via the accordion */}
                </ModalBody>
            </Modal>

            <Modal isOpen={actorModalOpen} size="md" toggle={() => setActorModalOpen(false)}>
                <ModalHeader toggle={() => setActorModalOpen(false)}>Actor</ModalHeader>
                <ModalBody>
                    {!selectedActor ? (
                        <div className="text-muted small">No actor details available.</div>
                    ) : (
                        <table className="w-100 small">
                            <tbody>
                                <tr>
                                    <td className="text-muted">First name</td>
                                    <td className="text-end fw-bold">{selectedActor?.first_name || "-"}</td>
                                </tr>
                                <tr>
                                    <td className="text-muted">Last name</td>
                                    <td className="text-end fw-bold">{selectedActor?.last_name || "-"}</td>
                                </tr>
                                <tr>
                                    <td className="text-muted">Email</td>
                                    <td className="text-end fw-bold">{selectedActor?.email || "-"}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}

